import { useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudUpload, Sync, Visibility } from '@mui/icons-material';
import {
  CustomSelect,
  GeneralTableBox,
  HeadTableCell,
  LoadingSkeleton,
  NoDataSkeleton,
  RowTableCell,
  PaginationTable,
} from '../../../common/components';
import { formatCurrency } from '../../../utils/formatUtils';
import { useUpdateDebtStatus } from '../../../hooks/useDebt';
import { DEBT_STATUS_OPTIONS } from '../constants/debtStatusConstants';

const DebtStatusTable = ({
  rows,
  loading,
  totalContracts,
  onReload,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  showAgreementActions = false,
  onUploadAgreement,
  onViewAgreement,
  uploadingAgreementContractUuid = null,
}) => {
  const updateDebtStatus = useUpdateDebtStatus();
  const [updatingContract, setUpdatingContract] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleStatusChange = async (row, nextStatus) => {
    if (!row?.contract_uuid || row.debt_status === nextStatus) {
      return;
    }

    setUpdatingContract(row.contract_uuid);
    try {
      await updateDebtStatus.mutateAsync({
        contractUuid: row.contract_uuid,
        debt_status: nextStatus,
      });
      setSnackbar({
        open: true,
        message: 'Estado de deuda actualizado correctamente.',
        severity: 'success',
      });
      if (onReload) {
        onReload();
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setSnackbar({
        open: true,
        message: apiMessage || 'No se pudo actualizar el estado de deuda.',
        severity: 'error',
      });
    } finally {
      setUpdatingContract(null);
    }
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!rows.length) {
    return (
      <GeneralTableBox>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeadTableCell>Contrato</HeadTableCell>
              <HeadTableCell>Suscriptor</HeadTableCell>
              <HeadTableCell>Plan</HeadTableCell>
              <HeadTableCell>Poseedor</HeadTableCell>
              <HeadTableCell>Forma pago</HeadTableCell>
              <HeadTableCell>Cupones mora</HeadTableCell>
              <HeadTableCell>Importe mora</HeadTableCell>
              <HeadTableCell>Estado</HeadTableCell>
              {showAgreementActions && <HeadTableCell>Acuerdo</HeadTableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            <NoDataSkeleton
              colSpan={showAgreementActions ? 9 : 8}
              message="No se encontraron registros que coincidan con los filtros seleccionados."
            />
          </TableBody>
        </Table>
      </GeneralTableBox>
    );
  }

  return (
    <Box>
      <GeneralTableBox>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeadTableCell>Contrato</HeadTableCell>
              <HeadTableCell>Suscriptor</HeadTableCell>
              <HeadTableCell>Plan</HeadTableCell>
              <HeadTableCell>Poseedor</HeadTableCell>
              <HeadTableCell>Forma pago</HeadTableCell>
              <HeadTableCell>Cupones mora</HeadTableCell>
              <HeadTableCell>Importe mora</HeadTableCell>
              <HeadTableCell>Estado</HeadTableCell>
              {showAgreementActions && <HeadTableCell>Acuerdo</HeadTableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const subscriberNames = Array.isArray(row.subscriber_names)
                ? row.subscriber_names
                : [];
              const activeAgreement = row.active_agreement || null;
              const hasAgreementFile = Boolean(
                activeAgreement?.file_url || activeAgreement?.file_ref
              );
              const isAgreementStatus = String(row.debt_status || '')
                .toLowerCase()
                .startsWith('acuerdo vigente');
              const isUploadingAgreement = uploadingAgreementContractUuid === row.contract_uuid;

              return (
                <TableRow key={row.contract_uuid} hover>
                  <RowTableCell>{row.contract_number}</RowTableCell>
                  <RowTableCell sx={{ maxWidth: '240px', minWidth: '240px' }}>
                    {subscriberNames.length > 0 ? (
                      subscriberNames.map((name, index) => (
                        <Typography
                          key={`${row.contract_uuid}-subscriber-${index}`}
                          variant="body2"
                          fontWeight={400}
                          fontSize={12}
                        >
                          {name}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2">Sin suscriptor</Typography>
                    )}
                  </RowTableCell>
                  <RowTableCell>{row.plan || 'Habitat 99'}</RowTableCell>
                  <RowTableCell>{row.is_possessor ? 'SI' : 'NO'}</RowTableCell>
                  <RowTableCell>{row.payment_method}</RowTableCell>
                  <RowTableCell>{row.overdue_installments}</RowTableCell>
                  <RowTableCell sx={{ maxWidth: '200px', minWidth: '200px' }}>
                    {formatCurrency(row.debt_amount)}
                  </RowTableCell>
                  <RowTableCell>
                    <CustomSelect
                      size="small"
                      value={row.debt_status}
                      onChange={(event) => handleStatusChange(row, event.target.value)}
                      sx={{ minWidth: 150 }}
                      disabled={updatingContract === row.contract_uuid}
                    >
                      {DEBT_STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </CustomSelect>
                  </RowTableCell>
                  {showAgreementActions && (
                    <RowTableCell>
                      {isAgreementStatus ? (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Tooltip
                            title={hasAgreementFile ? 'Reemplazar archivo' : 'Cargar archivo'}
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onUploadAgreement?.(row)}
                                disabled={isUploadingAgreement}
                              >
                                {hasAgreementFile ? (
                                  <Sync fontSize="small" />
                                ) : (
                                  <CloudUpload fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Ver archivo">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => onViewAgreement?.(row)}
                                disabled={!hasAgreementFile}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </RowTableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </GeneralTableBox>
      {pagination?.pages > 1 && (
        <PaginationTable
          totalPages={pagination.pages}
          totalCount={pagination.total}
          page={pagination.page}
          onPageChange={onPageChange}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DebtStatusTable;
